import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.trippang.com";

// 국가/도시/카테고리/메뉴/언어 등 참조 데이터는 자주 안 바뀌므로 모듈 레벨(세션) 캐시 + in-flight dedupe.
// 컴포넌트가 재마운트될 때마다(예: 배너 상세 페이지 → 뒤로가기) 재요청하지 않도록 함 —
// 아이폰 Safari 는 동일 호스트로 다수 fetch 를 동시에 쏘면 커넥션 재사용 문제로 요청이
// 수분간 멈추는 사례가 있어, 재마운트 시 네트워크 자체를 스킵하는 게 중요.
const _refCache = new Map<string, unknown>();
const _refInflight = new Map<string, Promise<unknown>>();
function cachedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  if (_refCache.has(key)) return Promise.resolve(_refCache.get(key) as T);
  const inflight = _refInflight.get(key) as Promise<T> | undefined;
  if (inflight) return inflight;
  const p = fetcher()
    .then((data) => { _refCache.set(key, data); _refInflight.delete(key); return data; })
    .catch((e) => { _refInflight.delete(key); throw e; });
  _refInflight.set(key, p);
  return p;
}

export interface Language {
  id: number;
  key: string;
  alpha2: string;
  alpha3: string;
  label: string;
  flag: string;
}

export interface Country {
  id: number;
  key: string;
  flag: string;
  phone_code: string;
  label: string;
}

export interface City {
  id: number;
  key: string;
  label: string;
}

export interface Category {
  id: number;
  key: string;
  label: string;
  description: string;
  icon_url?: string | null;
}

// 국가/카테고리/언어/전체도시 — /public/business/bootstrap 1회 요청으로 통합 로드.
// 요구사항: 최초 로딩/강력 새로고침 시 이 4개를 각각 fetch 하면 동시 요청이 몰려
// 사파리(맥/아이폰)에서 커넥션 재사용 지연(수분)이 발생 → lang 키 공유 캐시로 요청 수 최소화.
interface BootstrapData {
  countries: Country[];
  categories: Category[];
  languages: Language[];
  cities: (City & { country_id: number })[];
}
function loadBootstrap(lang: string): Promise<BootstrapData> {
  return cachedFetch(`bootstrap:${lang}`, () =>
    fetch(`${API_BASE}/public/business/bootstrap?lang=${lang}`).then((r) => r.json())
  );
}

export function useCategories() {
  const { i18n } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    let alive = true;
    loadBootstrap(i18n.language)
      .then((d) => { if (alive) setCategories(Array.isArray(d.categories) ? d.categories : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [i18n.language]);

  return categories;
}

// ───────────────────────── 메뉴 (헤더 햄버거 그리드) ─────────────────────────

export interface Menu {
  id: number;
  key: string;
  icon_url?: string | null;
  // "/" 시작 → 라우트 navigate, 그 외 → PopupKey 로 모달
  url?: string | null;
  requires_login: boolean;
  label: string;
  description?: string | null;
}

export function useMenus(): Menu[] {
  const { i18n } = useTranslation();
  const [menus, setMenus] = useState<Menu[]>([]);

  useEffect(() => {
    let alive = true;
    cachedFetch(`menus:${i18n.language}`, () =>
      fetch(`${API_BASE}/public/business/menus?lang=${i18n.language}`).then((r) => r.json())
    )
      .then((data) => { if (alive) setMenus(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setMenus([]); });
    return () => { alive = false; };
  }, [i18n.language]);

  return menus;
}

export function useAllCityTranslations() {
  const { i18n } = useTranslation();
  const [cityMap, setCityMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    // 요구사항 #558: 도시명도 선택 언어로 번역(예전 "영어 통일" 정책 폐기)
    loadBootstrap(i18n.language)
      .then((d) => {
        const map: Record<string, string> = {};
        (Array.isArray(d.cities) ? d.cities : []).forEach((c) => { map[c.key] = c.label; });
        if (alive) setCityMap(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [i18n.language]);

  return cityMap;
}

export function useLanguages() {
  const [languages, setLanguages] = useState<Language[]>([]);

  useEffect(() => {
    let alive = true;
    loadBootstrap("en")
      .then((d) => { if (alive) setLanguages(Array.isArray(d.languages) ? d.languages : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return languages;
}

export function useCountries() {
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    let alive = true;
    loadBootstrap("en")
      .then((d) => { if (alive) setCountries(Array.isArray(d.countries) ? d.countries : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return countries;
}

// ───────────────────────── 배너 (DB API) ─────────────────────────

/** DB 배너 API 응답 타입 */
export interface ApiBanner {
  id: number;
  banner_grade: number;     // 1:VIP 2:Platinum 3:Gold 4~6:Silver
  image_url: string | null;
  sort_order: number;
  city_key: string;
  country_key: string;
  category_key: string;
  title: string | null;
  subtitle: string | null;
  html: string | null;
  show_title?: boolean;
  show_subtitle?: boolean;
  show_city?: boolean;
  show_comment_count?: boolean;
  comment_count?: number;
  business_type?: string | null;
  business_name?: string | null;
  address?: string | null;
  business_hours?: string | null;
  phone_number?: string | null;
  phone_number_secondary?: string | null;
  image_sizes?: Record<string, string> | null;
  website?: string | null;
  sns?: string | null;
  kakao_url?: string | null;
  telegram_url?: string | null;
  show_favorite_count?: boolean;
  favorite_count?: number;
}

/**
 * 배너 목록 조회 (DB API) — 정수 ID 필터
 * @param countryId 국가 ID
 * @param cityId 도시 ID (null이면 전체 도시)
 * @param categoryId 카테고리 ID (null이면 전체)
 */
export function useBanners(countryId: number | null, cityId: number | null, categoryId: number | null, tag: string | null = null, maxGrade?: number, enabled: boolean = true): [ApiBanner[], boolean] {
  const { i18n } = useTranslation();
  const [banners, setBanners] = useState<ApiBanner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }  // catalog 모드 — 중복 fetch 스킵
    const params = new URLSearchParams({ lang: i18n.language });
    if (countryId) params.set("country_id", String(countryId));
    if (cityId) params.set("city_id", String(cityId));
    if (categoryId) params.set("category_id", String(categoryId));
    if (tag) params.set("tag", tag);
    if (maxGrade) params.set("max_grade", String(maxGrade));  // 실버 제외하여 소량만

    fetch(`${API_BASE}/public/business/banners?${params}`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setBanners(Array.isArray(data) ? data : []))
      .catch(() => setBanners([]))
      .finally(() => setLoading(false));
  }, [countryId, cityId, categoryId, tag, maxGrade, enabled, i18n.language]);

  return [banners, loading];
}

/** 실버(등급 6) 페이지네이션 — VirtuosoGrid 무한스크롤용. 보이는 구간만 fetch.
 * 서버가 order_at desc(최신 등록순, 점프 시 갱신)로 이미 확정 정렬해 보내므로 클라 재정렬 불필요(#403 후속) */
export function useSilverBanners(countryId: number | null, cityId: number | null, categoryId: number | null, tag: string | null = null, enabled: boolean = true) {
  const { i18n } = useTranslation();
  const PAGE = 30;
  const [items, setItems] = useState<ApiBanner[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const offsetRef = useRef(0);
  const doneRef = useRef(false);
  const loadingRef = useRef(false);

  // 필터/언어 변경 시 리셋
  const filterKey = `${countryId}|${cityId}|${categoryId}|${tag}|${i18n.language}`;

  const fetchPage = useCallback(async (reset: boolean) => {
    if (!enabled) return;  // catalog 모드 — 중복 fetch 스킵
    if (loadingRef.current) return;
    if (!reset && doneRef.current) return;
    loadingRef.current = true;
    const offset = reset ? 0 : offsetRef.current;
    const params = new URLSearchParams({ lang: i18n.language, grade: "6", offset: String(offset), limit: String(PAGE) });
    if (countryId) params.set("country_id", String(countryId));
    if (cityId) params.set("city_id", String(cityId));
    if (categoryId) params.set("category_id", String(categoryId));
    if (tag) params.set("tag", tag);
    try {
      const r = await fetch(`${API_BASE}/public/business/banners?${params}`);
      const tc = parseInt(r.headers.get("X-Total-Count") || "0", 10);
      const data = r.ok ? await r.json() : [];
      const arr: ApiBanner[] = Array.isArray(data) ? data : [];
      setTotal(tc);
      offsetRef.current = offset + arr.length;
      if (arr.length < PAGE || offsetRef.current >= tc) doneRef.current = true;
      setItems((prev) => (reset ? arr : [...prev, ...arr]));
    } catch {
      // silent — 다음 시도 가능하게 done 처리 안 함
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [countryId, cityId, categoryId, tag, enabled, i18n.language]);

  useEffect(() => {
    if (!enabled) { setItems([]); setTotal(0); setLoading(false); return; }  // catalog 모드 — 스킵
    offsetRef.current = 0;
    doneRef.current = false;
    setItems([]);
    setLoading(true);
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, enabled]);

  // 점프 실행 직후 즉시 순서 반영 — 실버 정렬이 order_at desc 이므로 첫 페이지부터 재조회
  useEffect(() => {
    if (!enabled) return;
    const onJumpDone = () => { offsetRef.current = 0; doneRef.current = false; fetchPage(true); };
    window.addEventListener("trippang:jump-done", onJumpDone);
    return () => window.removeEventListener("trippang:jump-done", onJumpDone);
  }, [enabled, fetchPage]);

  const loadMore = useCallback(() => { fetchPage(false); }, [fetchPage]);

  return { items, total, loading, loadMore };
}

/** 이벤트 배너 목록 조회 (banner_grade=5) */
export function useEventBanners(countryId: number | null, cityId: number | null, tag?: string | null, enabled: boolean = true): [ApiBanner[], boolean] {
  const { i18n } = useTranslation();
  const [banners, setBanners] = useState<ApiBanner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }  // catalog 모드 — 중복 fetch 스킵
    const params = new URLSearchParams({ lang: i18n.language, grade: "5" });
    if (countryId) params.set("country_id", String(countryId));
    if (cityId) params.set("city_id", String(cityId));
    if (tag) params.set("tag", tag);  // 검색 시 이벤트(grade5) 배너도 검색어로 필터

    fetch(`${API_BASE}/public/business/banners?${params}`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setBanners(Array.isArray(data) ? data : []))
      .catch(() => setBanners([]))
      .finally(() => setLoading(false));
  }, [countryId, cityId, tag, enabled, i18n.language]);

  return [banners, loading];
}

export function useCities(countryKey: string) {
  const { i18n } = useTranslation();
  const [cities, setCities] = useState<City[]>([]);

  useEffect(() => {
    if (!countryKey) { setCities([]); return; }
    let alive = true;
    // 요구사항 #558: 도시 드롭다운도 선택 언어로 번역
    loadBootstrap(i18n.language)
      .then((d) => {
        if (!alive) return;
        const country = (d.countries || []).find((c) => c.key === countryKey);
        setCities(country ? (d.cities || []).filter((c) => c.country_id === country.id) : []);
      })
      .catch(() => { if (alive) setCities([]); });
    return () => { alive = false; };
  }, [countryKey, i18n.language]);

  return cities;
}
